import { Button, Container, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AttachMoney, Description, GetApp } from "@material-ui/icons";
import Card from 'components/Card';
import useWidth from 'hooks/useWidth';
import { documentType, formatDocument, formatMoney, ticketSortFieldMap } from 'utils/functions';
import TableLoader from 'views/SalesReports/components/skletons/TableLoader';
import { Alert } from '@mui/material';
import { Table } from 'components';
import { useStyles } from '../styles/styles';
import SearchBar from 'material-ui-search-bar';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { asyncBoletos, asyncBoletosSummary } from 'store/reducers/ticket.reducer';
import { asyncReportsGenerate } from 'store/reducers/reports.reducer';
import DateRangeFilter from 'components/DateRangeFilter';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { routes } from 'utils/routers.constants';
import { format, isValid, parseISO } from 'date-fns';

const Ticket = () => {
  const dispatch = useDispatch();
  const classes  = useStyles();
  const displayWidth = useWidth();
  const history      = useHistory(); 
  const boletos      = useSelector(s => s.Ticket.boletos);
  const total        = useSelector(s => s.Ticket.boletosTotal);
  const loading      = useSelector(s => s.Ticket.loading);
  const summary      = useSelector(s => s.Ticket.boletosSummary);
  const loadingSummary = useSelector(s => s.Ticket.loadingSummary);
  const isExporting  = useSelector(s => s.Reports.isLoading);

  const [sort, setSort] = useState('created_at');
  const [orderBy, setOrderBy] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [appliedRange, setAppliedRange] = useState({ initialDate: startDate, endDate: endDate || startDate });

  const isDesktop = displayWidth > 820
  const columns = [
    { id: 'plan_name', label: 'Nome do plano', minWidth: 170, orderable: false },
    { id: 'plan_id', label: 'ID do plano', minWidth: 130, orderable: false },
    { id: 'installment', label: 'Parcela do boleto', minWidth: 170, orderable: false },
    { id: 'status', label: 'Status', minWidth: 130, orderable: false },
    { id: 'document', label: 'Documento', minWidth: 170, orderable: false },
    { id: 'cispay_client', label: 'Cliente', minWidth: 150, orderable: false},
    { id: 'payment_method', label: 'Pago por', minWidth: 150, orderable: false },
    { id: 'value', label: 'Valor', minWidth: 130, orderable: false },
    { id: 'created_at', label: 'Data e hora de criação', minWidth: 200 },
    { id: 'paid_at', label: 'Data e hora do pagamento', minWidth: 200, orderable: true },
    { id: 'due_date', label: 'Data do vencimento', minWidth: 170, orderable: true },
    {id: 'disabled', label: 'Plano de pagamento', minWidth: 130, orderable: false},
  ];

  const column = columns.find(c => c.id === sort);
  column.orderBy = orderBy;
        
  const onSort = (id) => {
    setOrderBy(orderBy === 'asc' ? 'desc' : 'asc');
    setSort(id);
  };

  useEffect(() => {
    dispatch(asyncBoletos({
      page: page,
      pageSize,
      status,
      search,
      sort,
      orderBy,
      dateFrom: appliedRange.initialDate,
      dateTo: appliedRange.endDate,
    }));
  }, [page, pageSize, status, search, sort, orderBy, appliedRange]);

  useEffect(() => {
    dispatch(asyncBoletosSummary({
      dateFrom: startDate,
      dateTo: endDate,
    }));
  }, [dispatch]);


  const handleRead = (boleto) => {
    const url = `${routes.READPLAN.replace(':id', boleto.id)}`;
    history.push(url);
  };

  const handlePageSize = (pageSize) => {
    setPageSize(pageSize);
    setPage(0);
  };


  const handleApplyDateFilter = ({ startDate, endDate }) => {
    setPage(0);
    setAppliedRange({ initialDate: startDate, endDate: endDate || startDate });

    dispatch(asyncBoletos({
      page: 0,
      pageSize,
      status,
      search,
      sort,
      orderBy, 
      dateFrom: startDate || null,
      dateTo: endDate || null,
    }));

    dispatch(asyncBoletosSummary({
      dateFrom: startDate || null,
      dateTo: endDate || null,
    }));
  }

  const handleCurrentPage = (page) => {
    setPage(page);
  };

  const exportDisabled = loading || isExporting || !total;

  const handleExport = useCallback(() => {
    if (exportDisabled) return;

    dispatch(asyncReportsGenerate({
      type: 'BOLETOS',
      parameters: {
        ...(search.trim() && { search: search.trim() }),
        ...(status !== 'all' && { status }),
        ...appliedRange,
        sort: ticketSortFieldMap[sort],
        order: orderBy.toUpperCase(),
      },
    }));
  }, [dispatch, exportDisabled, search, status, appliedRange, sort, orderBy]);

  const boletoStatus = {
    Paid: 'Pago',
    Open: 'Em aberto',
    Overdue: 'Vencido',
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const d = parseISO(isoString);
    return isValid(d) ? format(d, "dd/MM/yyyy 'às' HH:mm") : '';
  };

  const BoletosFormatted = useMemo(() => {
  return boletos.map((boleto) => {
    const isCanceled = boleto.status === 'Canceled';
    const label = boletoStatus[boleto.status] ?? boleto.status;

    return {
      id: boleto.payment_plan_id,
      plan_name: boleto.payment_plan_name,
      plan_id: boleto.payment_plan_id,
      installment: boleto.installments,
      status: (
        <div className={isCanceled ? classes.bgStatusError : classes.bgStatusPrimary}>
          {label}
        </div>
      ),
      document: formatDocument({type: boleto?.customer?.document?.type, number: boleto?.customer?.document?.number}),
      cispay_client: boleto?.customer?.document?.type === documentType.CPF ? `${boleto?.customer?.firstName} ${boleto?.customer?.lastName}` :  boleto?.customer?.corporateName ,
      payment_method: boleto.payment_method,
      value: formatMoney(boleto.price),
      created_at: formatDateTime(boleto.created_at),
      paid_at: formatDateTime(boleto.payment_date),
      due_date: formatDateTime(boleto.expiration_date),
      disabled: (
        <span className={classes.badgeDisabled}>
          {(boleto.payment_plan_enabled ? 'Habilitado' : 'Desabilitado')}
        </span>
      ),
      };
    });
  }, [boletos, classes]);

    return (
        <Container className={classes.main} maxWidth="xl" component='main'>
          <Grid
            container
            component='header'
            direction="row"
            justify="space-between"
            alignItems="flex-start"
            className={classes.header}
          >
          <Grid item xs={12} md={4}>
          <Typography variant="h4" className={classes.title_header}>
            Boletos
          </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={1} alignItems="flex-start" justify="flex-end">      
              <Grid item xs={12} md="auto">                    
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onApply={handleApplyDateFilter}
              />
              </Grid>
              <Grid item xs={12} md="auto">
                <Button
                  color="primary"
                  startIcon={<GetApp />}
                  onClick={handleExport}
                  aria-disabled={exportDisabled}
                  aria-busy={isExporting}
                  style={exportDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                >
                  Exportar XLSX
                </Button>
              </Grid>
            </Grid>
            </Grid>
          </Grid>
          <Typography variant="h6" component="h2" className={classes.title}>
              Total de boletos
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={isDesktop ? 3 : 2}>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card
                title='Total de boletos gerados' 
                value={summary.generated || 0} 
                borderLeftColor='#8FA3BF' 
                iconBackgroundColor='#DCE4EE' 
                icon={<Description style={{ color: "#8FA3BF", fontSize: 20 }} />}
                isLoading={loadingSummary}
                secondValueLabel={'Total:'}
                secondValue={summary.totalGenerated ? formatMoney(summary.totalGenerated) : 'R$ 0,00' }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card 
                title='Total de boletos vencidos'
                value={summary.overdue || 0} 
                borderLeftColor='#8E7DBE'
                iconBackgroundColor='#E5E0F4' 
                icon={<Description style={{ color: "#8E7DBE", fontSize: 20 }} />}
                isLoading={loadingSummary}
                secondValueLabel={'Total:'}
                secondValue={summary.totalOverdue ? formatMoney(summary.totalOverdue) : 'R$ 0,00' }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card 
                title='Total de boletos pagos'
                value={summary.paid || 0} 
                borderLeftColor='#4C89AC' 
                iconBackgroundColor='#CFE0EB'
                icon={<AttachMoney style={{ color: "#4C89AC", fontSize: 20 }} />}
                isLoading={loadingSummary}
                secondValueLabel={'Total:'}
                secondValue={summary.totalPaid ? formatMoney(summary.totalPaid) : 'R$ 0,00' }
              />
            </Grid>
          </Grid>
          <Typography variant="h6" component="h2" className={classes.title}>
              Listagem
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={1} alignItems="center" className={classes.searchRow}>
            <Grid item xs>
              <SearchBar
                value={search}
                placeholder="Pesquise por nome do plano, ID do plano, nome do cliente ou documento"
                className={classes.search}
                onChange={(text) => { setPage(0); setSearch(text); }}
                onCancelSearch={() => setSearch('')}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <FormControl
                variant="filled"
                className={classes.statusControl}
                fullWidth
              >
                <InputLabel>Filtro por status:</InputLabel>
                <Select
                className={classes.selectItem}
                  value={status}
                  onChange={(e) => { setPage(0); setStatus(e.target.value); }}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {Object.entries(boletoStatus).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TicketTable
            readButton = 'Visualizar'
            actionDisabled={(row) => row.status.props.children === 'Cancelado'}
            onClickRead = {handleRead}
            error={true}
            columns={columns}
            rows={BoletosFormatted}
            count={total}
            selectRow={false}
            currentPage={page}
            pageSize={pageSize}
            basicPagination={false}
            rowsPerPageOptions={[10, 25, 50]}
            onPageSize={handlePageSize}
            sortable
            onSort={(e, id) => onSort(id)}
            loading={loading}
            onCurrentPage={handleCurrentPage}
          />     
        </Container>
    )
}

export default Ticket


const TicketTable = ({loading, rows, ...props}) => {
  const classes = useStyles();

  if (loading) {
    return <TableLoader />
  }

  if (!rows?.length) {
    return <Alert severity="info" className={classes.alert}>Nenhum boleto gerado!</Alert>
  }

  return (
    <Table
      rows={rows}
      {...props}
    />
  )
}